import { Box, Button, Card, CardBody, Flex, Icon, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Skeleton, Stack, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { Tooltip } from "../../../common/components/Tooltip";
import { runsUnderUtilities } from "./runsUnderUtilities";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight, FiShoppingCart } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  type SourcingEventDto,
  useCreateDpsCompetitionMutation,
  useGetDpsCompetitionsQuery,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { urls } from "../../../urls";
import { SourcingEventStatusTag } from "../SourcingEventStatusTag";

interface Props {
  event: SourcingEventDto;
}

// Each purchase under the ordning (FOA § 26-7). The UI calls it a «konkurranse»; the formal term
// belongs in the protocol, not on a button.
export const DpsCompetitionsTab = ({ event }: Props) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const displayDateWithTime = useDisplayDateWithTime();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetDpsCompetitionsQuery({ eventId: event.id });
  const [isStarting, setIsStarting] = useState(false);

  if (isLoading) {
    return (
      <Stack spacing="4" aria-busy>
        <Skeleton height="10" rounded="md" />
        <Skeleton height="32" rounded="xl" />
      </Stack>
    );
  }
  if (isError || !data) {
    return (
      <Flex direction="column" align="center" py="10" gap="3">
        <Text color="smError">{t("Could not load the competitions.")}</Text>
        <Button variant="outline" onClick={() => refetch()}>
          {t("Try again")}
        </Button>
      </Flex>
    );
  }

  const rows = data.rows ?? [];
  // The validity has two ends: the system can be waiting to start as easily as it can be finished.
  const scheme = event.eventContext.type === "Public" ? event.eventContext.dpsScheme : null;
  const now = Date.now();
  const notStartedYet = scheme != null && Date.parse(scheme.validFrom) > now;
  const blockedReason =
    notStartedYet ? t("The system runs from {{date}} — competitions can start then.", {
      date: displayDate(scheme.validFrom),
    })
    : scheme != null && Date.parse(scheme.validTo) <= now ?
      t("The system closed on {{date}}.", { date: displayDate(scheme.validTo) })
    : runsUnderUtilities(event)
    ? t("At least one supplier must be qualified before a competition can start (forsyningsforskriften § 22-5).")
    : t("At least one supplier must be qualified before a competition can start (FOA § 26-7).");

  return (
    <Stack spacing="5">
      <Flex align="center" justify="space-between" gap="4" wrap="wrap">
        <Text fontSize="sm" color="smMuted">
          {data.canStartCompetition ? t("Every supplier qualified when a competition starts is invited to it.") : blockedReason}
        </Text>
        <Tooltip label={data.canStartCompetition ? undefined : blockedReason} shouldWrapChildren>
          <Button
            colorScheme="teal"
            isDisabled={!data.canStartCompetition}
            onClick={() => setIsStarting(true)}
          >
            {t("New competition")}
          </Button>
        </Tooltip>
      </Flex>

      {rows.length === 0 ? (
        <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder">
          <CardBody py="10">
            <Flex direction="column" align="center" gap="3" textAlign="center">
              <Icon as={FiShoppingCart} boxSize="8" color="smPrimary" />
              <Text fontWeight="semibold" color="smText">
                {t("No competitions have been run yet")}
              </Text>
              <Text fontSize="sm" color="smMuted" maxW="md">
                {data.canStartCompetition
                  ? t("Start one whenever you need to buy — no new notice is needed.")
                  : blockedReason}
              </Text>
            </Flex>
          </CardBody>
        </Card>
      ) : (
        <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" overflowX="auto">
          <Table size="md">
            <Thead>
              <Tr>
                <Th>{t("Competition")}</Th>
                <Th>{t("Status")}</Th>
                <Th>{t("Bid deadline")}</Th>
                <Th>{t("Invited")}</Th>
                <Th aria-hidden />
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr
                  key={row.eventId}
                  cursor="pointer"
                  _hover={{ bg: "smBackgroundSecondary" }}
                  onClick={() => navigate(urls.app.sourcingEvents.view.go(row.eventId))}
                >
                  <Td>
                    <Text fontWeight="semibold" fontSize="sm" color="smText" noOfLines={1}>
                      {row.title}
                    </Text>
                  </Td>
                  <Td>
                    <SourcingEventStatusTag status={row.status} />
                  </Td>
                  <Td fontSize="sm" color="smMuted" whiteSpace="nowrap">
                    {row.bidSubmissionDeadline ? displayDateWithTime(row.bidSubmissionDeadline) : "–"}
                  </Td>
                  <Td fontSize="sm" color="smMuted" fontFamily="mono">
                    {row.invitedCount}
                  </Td>
                  <Td textAlign="right">
                    <Icon as={FiChevronRight} color="smMuted" />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {isStarting && <StartCompetitionModal schemeId={event.id} onClose={() => setIsStarting(false)} />}
    </Stack>
  );
};

const StartCompetitionModal = ({ schemeId, onClose }: { schemeId: string; onClose: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const [title, setTitle] = useState("");
  const [create, { isLoading }] = useCreateDpsCompetitionMutation();

  const submit = async () => {
    try {
      const created = await create({
        eventId: schemeId,
        createDpsCompetitionRequestDto: { title },
      }).unwrap();
      navigate(urls.app.sourcingEvents.edit.go(created.id).info);
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Modal isOpen onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("New competition")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="smMuted" mb="3">
            {t("The award criteria and classification come from the system. You set the need and the deadline.")}
          </Text>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("What are you buying?") ?? undefined}
            autoFocus
          />
        </ModalBody>
        <ModalFooter gap="2" justifyContent="flex-start" flexDirection="row-reverse">
          <Button
            colorScheme="teal"
            isDisabled={title.trim().length === 0}
            isLoading={isLoading}
            onClick={submit}
          >
            {t("Create")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
